// src/components/legal/AgreementGate.jsx
import React, { useEffect, useState } from "react";
import useDialog from "@/hooks/useDialog";
import { ExternalLink } from "lucide-react";
import { fetchWithAuth } from "../../utils/fetchWithAuth";

export default function AgreementGate({ slug = "tos", children }) {
  const [loading, setLoading] = useState(true);
  const [needs, setNeeds] = useState(false);
  const [agreement, setAgreement] = useState(null);
  const [checked, setChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  // Required: no Escape. Focus and Tab stay in the gate.
  const dialog = useDialog({ open: !loading && needs });

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        // fetchWithAuth already returns parsed JSON
        const data = await fetchWithAuth(`/api/accounts/legal/status/?slug=${slug}`);
        
        if (!mounted) return;
        
        setNeeds(Boolean(data?.needs_acceptance));
        setAgreement(data?.agreement || null);
      } catch (error) {
        console.error("Failed to load agreement status:", error);
        if (mounted) setErr("Failed to load agreement status.");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    
    return () => { mounted = false; };
  }, [slug]);

  const accept = async () => {
    if (!agreement?.id || !checked) return;
    
    setSubmitting(true);
    setErr("");
    
    try {
      await fetchWithAuth("/api/accounts/legal/accept/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ agreement_id: agreement.id }),
      });
      
      setNeeds(false);
    } catch (error) {
      console.error("Failed to accept agreement:", error);
      setErr("Failed to record acceptance. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center">
        <div className="text-tertiary text-lg">Loading...</div>
      </div>
    );
  }

  if (!needs) return <>{children}</>;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      {/* A link, not a wall of text.

          This used to render the entire agreement into a scroll box between a
          new account and the product. Nobody reads eight screens of legal copy
          in a 60vh window on a phone, so what it actually bought was a page of
          dense grey text at the exact moment somebody is deciding whether this
          was worth signing up for.

          Dropping the embedded copy does not weaken the acceptance. What makes
          a clickwrap hold up is conspicuous notice of the terms plus an
          unambiguous act of assent, and a prominent link sitting directly above
          the checkbox is the textbook form of it -- better, if anything, than
          burying the same text in a box most people scroll past. The parts that
          matter are all still here and all still recorded: the version, the
          link to that version, an unchecked box the person has to tick, and a
          server-side acceptance row tied to `agreement.id`. */}
      <div
        ref={dialog.ref}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="agreement-title"
        className="w-full max-w-md bg-surface text-ink border border-line rounded-2xl overflow-hidden shadow-2xl"
      >
        {/* Header */}
        <div className="p-5 sm:p-6 border-b border-line bg-surface">
          <h2 id="agreement-title" className="text-2xl font-display italic text-ink">
            {agreement?.title || "User Agreement"}
          </h2>
          <p className="text-sm text-tertiary mt-1">
            Version {agreement?.version}
          </p>
        </div>

        {/* Footer */}
        <div className="p-5 sm:p-6">
          <p className="text-sm text-secondary">
            Before you carry on, have a look at the terms you're agreeing to.
          </p>

          {/* New tab, deliberately: this dialog is the last thing between them
              and the account they just made, and navigating away from it would
              lose the acceptance step entirely. */}
          <a
            href={`/legal/${agreement?.slug || slug}`}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-3 inline-flex items-center gap-1.5 text-brand font-medium hover:underline"
          >
            Read the {agreement?.title || "terms"}
            <ExternalLink size={14} strokeWidth={2} aria-hidden="true" />
            <span className="sr-only">(opens in a new tab)</span>
          </a>

          <label className="flex items-start gap-3 mt-5 mb-4 cursor-pointer">
            <input
              type="checkbox"
              checked={checked}
              onChange={(e) => setChecked(e.target.checked)}
              className="mt-1 h-5 w-5 rounded border-line accent-brand focus:ring-brand"
            />
            <span className="text-sm text-secondary">
              I have read and agree to the {agreement?.title || "terms and conditions"}
            </span>
          </label>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
            {err && (
              <div className="text-sm text-red-700 flex-1">
                ⚠️ {err}
              </div>
            )}
            <button
              onClick={accept}
              disabled={!checked || submitting}
              className="w-full sm:w-auto sm:ml-auto bg-brand hover:bg-brand-hover text-white disabled:opacity-50 disabled:cursor-not-allowed font-semibold rounded-pill px-6 py-3 min-h-[44px] transition-all"
            >
              {submitting ? "Processing..." : "I Accept"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
} 